import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import PageTitle from "../elements/PageTitle";
import WorkoutScheduleTable from "../components/WorkoutScheduleTable";
import apiClient from "../api/apiClient";
import { useAuth } from "../auth/AuthContext";

const MySchedulePage = () => {
  const { user } = useAuth();
  const [plan, setPlan]       = useState(undefined); // undefined = loading, null = none
  const [loading, setLoading] = useState(true);
  const [videoLoading, setVideoLoading] = useState(false);

  const loadSchedule = () => {
    setLoading(true);
    apiClient.get("/my-schedule")
      .then(({ data }) => setPlan(data.plan ?? null))
      .catch(() => setPlan(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!user) { setLoading(false); return; }

    loadSchedule();
  }, [user]);

  // Called by WorkoutScheduleTable when user clicks an accessible video
  const handlePlayVideo = async (video, onReady) => {
    setVideoLoading(true);
    try {
      const res = await apiClient.get(`/videos/${video.slug}`);
      const embedUrl = res.data.data?.embed_url;
      if (embedUrl) onReady(embedUrl, video.title);
    } catch {
      // silently ignore
    } finally {
      setVideoLoading(false);
    }
  };

  const handleSaveExerciseLog = async (exercise, payload) => {
    const res = await apiClient.post(`/my-schedule/exercises/${exercise.assignment_id}/log`, payload);
    const updatedLog = res.data.log;

    setPlan((current) => {
      if (!current) return current;

      const days = Object.fromEntries(
        Object.entries(current.days || {}).map(([day, videos]) => [
          day,
          videos.map((video) => (
            video.assignment_id === exercise.assignment_id
              ? { ...video, user_log: updatedLog }
              : video
          )),
        ]),
      );

      return { ...current, days };
    });

    return updatedLog;
  };

  return (
    <div className="page-content bg-white">
      <PageTitle activePage="My Schedule" parentTitle="Dashboard" />

      <div className="content-inner-1">
        <div className="container">

          {/* Not logged in */}
          {!user && (
            <div className="text-center py-5">
              <i className="fa-solid fa-lock" style={{ fontSize: 48, color: "var(--primary)", marginBottom: 16 }} />
              <h4>Login Required</h4>
              <p className="text-muted mb-4">Please log in to view your personalised workout schedule.</p>
              <Link to="/login" className="btn btn-primary btn-skew">
                <span>Login</span>
              </Link>
            </div>
          )}

          {/* Loading */}
          {user && loading && (
            <div className="text-center py-5">
              <div className="spinner-border text-danger" role="status" />
              <p className="mt-3 text-muted">Loading your schedule…</p>
            </div>
          )}

          {/* Loaded */}
          {user && !loading && (
            <>
              <div className="section-head text-center mb-5">
                <span className="sub-title">Personalised</span>
                <h2 className="title">My Workout Schedule</h2>
                <p className="text-muted">
                  Your coach has prepared this weekly plan just for you.
                  Click any video to start watching — locked videos require an active subscription.
                </p>
              </div>

              <div className="schedule-table table-responsive">
                <WorkoutScheduleTable
                  plan={plan}
                  onPlayVideo={handlePlayVideo}
                  onSaveExerciseLog={handleSaveExerciseLog}
                  videoLoading={videoLoading}
                />
              </div>
            </>
          )}

        </div>
      </div>
    </div>
  );
};

export default MySchedulePage;
